// Ubicación en el repo: src/lib/patientsExport.js
// Exportar listado de pacientes a Excel (Configuración → Exportar,
// DECISIONS.md #118, pedido de Nicolás 16-sep-2026, tema 5 de 5).
//
// A diferencia de listPatients() (src/lib/patients.js), que lee de la vista
// patient_list_stats (pensada para el listado paginado, no trae todos los
// campos personales), acá se consulta `patients` directo con los campos que
// pidió Nicolás: datos personales + contacto. La seguridad de qué filas ve
// cada profesional la sigue resolviendo RLS sobre `patients` (mismo criterio
// que el resto del proyecto) — acá no se agrega ningún filtro de sucursal,
// se exporta todo a lo que el profesional ya tiene acceso.
//
// Se genera 100% en el navegador con la librería `xlsx` (SheetJS, agregada
// como dependencia nueva en package.json) — no pasa por Storage ni por
// ninguna Edge Function, es solo dar formato a datos que el profesional ya
// podía leer uno por uno en Pacientes.
import * as XLSX from 'xlsx';
import { supabase } from './supabaseClient';
import { formatPatientName, formatDate, todayISO } from './format';

function formatPrevision(p) {
  if (!p.prevision) return '';
  if (p.prevision === 'Isapre' && p.prevision_detalle) return `${p.prevision} (${p.prevision_detalle})`;
  return p.prevision;
}

// Devuelve la cantidad de pacientes exportados (para el mensaje de éxito en
// la pantalla) y dispara la descarga del .xlsx directamente.
export async function downloadPatientsExcel() {
  const { data, error } = await supabase
    .from('patients')
    .select(
      'nombre, segundo_nombre, apellido, segundo_apellido, rut, birthdate, sexo, phone, email, address, prevision, prevision_detalle, emergency_contact_name, emergency_contact_phone, patient_branches(branches(name))'
    )
    .is('deleted_at', null)
    .order('apellido', { ascending: true })
    .order('nombre', { ascending: true });
  if (error) throw error;

  const patients = data ?? [];

  const rows = patients.map((p) => ({
    'Nombre completo': formatPatientName(p),
    RUT: p.rut || '',
    'Fecha de nacimiento': p.birthdate ? formatDate(p.birthdate) : '',
    Sexo: p.sexo || '',
    Teléfono: p.phone || '',
    Correo: p.email || '',
    Dirección: p.address || '',
    Previsión: formatPrevision(p),
    'Contacto de emergencia': p.emergency_contact_name || '',
    'Teléfono de emergencia': p.emergency_contact_phone || '',
    'Sucursal(es)': (p.patient_branches || [])
      .map((pb) => pb.branches?.name)
      .filter(Boolean)
      .join(', '),
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 28 }, // Nombre completo
    { wch: 14 }, // RUT
    { wch: 15 }, // Fecha de nacimiento
    { wch: 12 }, // Sexo
    { wch: 14 }, // Teléfono
    { wch: 26 }, // Correo
    { wch: 32 }, // Dirección
    { wch: 18 }, // Previsión
    { wch: 24 }, // Contacto de emergencia
    { wch: 18 }, // Teléfono de emergencia
    { wch: 30 }, // Sucursal(es)
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pacientes');
  XLSX.writeFile(wb, `pacientes-${todayISO()}.xlsx`);

  return rows.length;
}
